import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { Stop } from '../../../../../../domain/stop/Stop';
import { useUpdateDriverStops } from '../../../hooks/driver/useUpdateDriverStops';
import { useRetrieveActivePlanDrivers } from '../../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../hooks/plan-details/useRetrieveActivePlanStops';
import { DriverPanel } from './DriverPanel';

export const driverColumnUnassigned = `unassigned`;
export type DriverColumnUnassignedTypes = `unassigned`;
export const EDIT_COLUMN_WIDTH = 380;

export interface DriverColumn {
  tab_index: number;
  driver_id: string | DriverColumnUnassignedTypes;
}

const PlanOverviewEditContainer = styled(Grid)`
  position: relative;
  height: 100%;
  flex-wrap: nowrap !important;
  max-width: ${EDIT_COLUMN_WIDTH * 2}px;
`;

interface PlanOverviewEditProps {
  selectedStops: string[];
  focusedStop?: Stop;
  onSelectStops: (stopIds: string[]) => void;
}

export const PlanOverviewEdit = ({ selectedStops, focusedStop, onSelectStops }: PlanOverviewEditProps) => {
  const [driverColumns, setDriverColumns] = useState<DriverColumn[]>([]);
  const { map: driversMap, data: drivers } = useRetrieveActivePlanDrivers();
  const { map: stopsMap } = useRetrieveActivePlanStops();

  const { run: updateDriverStops } = useUpdateDriverStops();

  useEffect(() => {
    if (driverColumns.length === 0) {
      const driversCount = (drivers ?? []).length;
      setDriverColumns([
        {
          driver_id: driversCount > 0 ? drivers![0].id : driverColumnUnassigned,
          tab_index: 0,
        },
        ...(driversCount > 0
          ? [
              {
                driver_id: driversCount > 1 ? drivers![1].id : driverColumnUnassigned,
                tab_index: 1,
              },
            ]
          : []),
      ]);
    }
  }, [drivers, driverColumns]);

  const onDriverChange = (driverId: string, index: number) => {
    const newDriverColumns = [...driverColumns];
    newDriverColumns[index].driver_id = driverId;
    setDriverColumns(newDriverColumns);
  };

  return (
    <PlanOverviewEditContainer
      wrap="nowrap"
      container
      direction="row"
      style={{ width: `${EDIT_COLUMN_WIDTH * driverColumns.length}px` }}
    >
      <DragDropContext
        onDragStart={(initial) => {
          if (!selectedStops.includes(initial.draggableId)) {
            onSelectStops([initial.draggableId]);
          }
        }}
        onDragEnd={({ draggableId: stopId, source, destination }) => {
          if (destination) {
            const { droppableId: driverIdTo, index: indexTo } = destination;
            const newDriverStops = selectedStops.map((stopId) => stopsMap[stopId]).filter((stop) => stop !== undefined);
            updateDriverStops({ driver: driversMap[driverIdTo], stops: newDriverStops, index: indexTo });
          }
        }}
      >
        {driverColumns.map((driverColumn, idx) => (
          <DriverPanel
            key={`driver-column-${driverColumn.driver_id}-${idx}`}
            focusedStop={focusedStop}
            selectedStops={selectedStops}
            driverColumnsIds={driverColumns.map((driverColumn) => driverColumn.driver_id)}
            driverColumn={driverColumn}
            onDriverChange={(newDriverId) => onDriverChange(newDriverId, idx)}
            setSelectedStops={onSelectStops}
          />
        ))}
      </DragDropContext>
    </PlanOverviewEditContainer>
  );
};
